// components/RecurringExpensesPopup.jsx
import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, User, CheckSquare, AlertCircle, Loader2 } from 'lucide-react';
import { createRecurringExpenses, stopPermanentPayment } from '../../Fetch/fetchExpenses';
import styles from './RecurringExpensesPopup.module.css';

const RecurringExpensesPopup = ({ 
    expenses, 
    isOpen, 
    onClose, 
    onExpensesCreated, 
    buildingId 
}) => {
    const [selectedExpenses, setSelectedExpenses] = useState(new Set());
    const [isCreating, setIsCreating] = useState(false);
    const [stoppingPayments, setStoppingPayments] = useState(new Set());
    const [message, setMessage] = useState({ type: '', text: '' });
    const [invoiceNumbers, setInvoiceNumbers] = useState({});
    const [paymentMethods, setPaymentMethods] = useState({});
    const [displayedExpenses, setDisplayedExpenses] = useState([]);

    // Payment method options
    const paymentMethodOptions = [
        'מזומן',
        'צ\'ק', 
        'העברה בנקאית',
        'ביט'
    ];

    // Auto-select all expenses when popup opens and set displayed expenses
    useEffect(() => {
        if (isOpen && expenses && expenses.length > 0) {
            setDisplayedExpenses([...expenses]);
            setSelectedExpenses(new Set(expenses.map(expense => expense.expenseID || expense.ExpenseID)));
            
            // Initialize payment methods with existing values or default to 'מזומן'
            const initialPaymentMethods = {};
            const initialInvoiceNumbers = {};
            expenses.forEach(expense => {
                const expenseId = expense.expenseID || expense.ExpenseID;
                initialPaymentMethods[expenseId] = expense.paymentMethod || expense.PaymentMethod || 'מזומן';
                initialInvoiceNumbers[expenseId] = '0';
            });
            setPaymentMethods(initialPaymentMethods);
            setInvoiceNumbers(initialInvoiceNumbers);
        }
    }, [isOpen, expenses]);

    if (!isOpen || !displayedExpenses || displayedExpenses.length === 0) return null;

    const handleExpenseToggle = (expenseId) => {
        const newSelected = new Set(selectedExpenses);
        if (newSelected.has(expenseId)) {
            newSelected.delete(expenseId);
        } else {
            newSelected.add(expenseId);
        }
        setSelectedExpenses(newSelected);
    };

    const handleInvoiceNumberChange = (expenseId, value) => {
        setInvoiceNumbers(prev => ({
            ...prev,
            [expenseId]: value
        }));
    };

    const handlePaymentMethodChange = (expenseId, value) => {
        setPaymentMethods(prev => ({
            ...prev,
            [expenseId]: value
        }));
    };

    const handleSelectAll = () => {
        if (selectedExpenses.size === displayedExpenses.length) {
            setSelectedExpenses(new Set());
        } else {
            setSelectedExpenses(new Set(displayedExpenses.map(expense => expense.expenseID || expense.ExpenseID)));
        }
    };

    const handleCreateExpenses = async () => {
        if (selectedExpenses.size === 0) {
            setMessage({ type: 'error', text: 'אנא בחר לפחות הוצאה אחת ליצירה' });
            return;
        }

        // Check if all selected expenses have invoice numbers
        const missingInvoices = Array.from(selectedExpenses).filter(
            expenseId => !invoiceNumbers[expenseId] || invoiceNumbers[expenseId].trim() === ''
        );

        if (missingInvoices.length > 0) {
            setMessage({ type: 'error', text: 'אנא הכנס מספר חשבונית לכל ההוצאות שנבחרו' });
            return;
        }

        setIsCreating(true);
        setMessage({ type: '', text: '' });

        try {
            const expenseIds = Array.from(selectedExpenses);
            
            console.log('Selected expenses:', expenseIds);
            console.log('Invoice numbers:', invoiceNumbers);
            
            // Create array of complete expense objects with new invoice numbers
            const expensesData = expenseIds.map(id => {
                // Find the original expense data
                const originalExpense = displayedExpenses.find(exp => 
                    (exp.expenseID || exp.ExpenseID) === id
                );
                
                if (!originalExpense) {
                    console.error('Could not find expense with ID:', id);
                    return null;
                }
                
                // Return complete ExpensesDTO object with all fields
                return {
                    ExpenseID: originalExpense.expenseID || originalExpense.ExpenseID,
                    BuildingID: originalExpense.buildingID || originalExpense.BuildingID,
                    Date: originalExpense.date || originalExpense.ExpenseDate || originalExpense.Date,
                    Category: originalExpense.category || originalExpense.Category || '',
                    Description: originalExpense.description || originalExpense.Description || '',
                    Amount: originalExpense.amount || originalExpense.Amount || 0,
                    PaidTo: originalExpense.paidTo || originalExpense.PaidTo || '',
                    ExpenseType: originalExpense.expenseType || originalExpense.ExpenseType || '',
                    PaymentMethod: paymentMethods[id] || 'מזומן', // Use the selected payment method
                    ReceiptImageURL: originalExpense.receiptImageURL || originalExpense.ReceiptImageURL || '',
                    SupplierID: originalExpense.supplierID || originalExpense.SupplierID || null,
                    Vendor: originalExpense.vendor || originalExpense.Vendor || '',
                    InvoiceNumber: invoiceNumbers[id] || '', // Use the new invoice number
                    StopPermanentPayment: false
                };
            }).filter(expense => expense !== null); // Remove any null entries
            
            console.log('Expenses data to send:', expensesData);
            
            if (expensesData.length === 0) {
                setMessage({ type: 'error', text: 'לא נמצאו נתוני הוצאות' });
                return;
            }
            
            const result = await createRecurringExpenses(expensesData);

            if (result.success) {
                setMessage({ 
                    type: 'success', 
                    text: `נוצרו בהצלחה ${result.data.length} הוצאות חוזרות לחודש הנוכחי` 
                });
                
                // Remove created expenses from selected
                setSelectedExpenses(new Set());
                
                // Notify parent component
                if (onExpensesCreated) {
                    onExpensesCreated(result.data);
                }

                // Auto-close after 2 seconds
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setMessage({ type: 'error', text: 'שגיאה ביצירת הוצאות חוזרות' });
            }
        } catch (error) {
            console.error('Error creating recurring expenses:', error);
            setMessage({ type: 'error', text: 'שגיאה ביצירת הוצאות חוזרות' });
        } finally {
            setIsCreating(false);
        }
    };

    const handleStopPayment = async (expenseId, supplierName) => {
        if (!window.confirm(`האם אתה בטוח שברצונך להפסיק את התשלום הקבוע ל${supplierName}?`)) {
            return;
        }

        setStoppingPayments(prev => new Set([...prev, expenseId]));

        try {
            const result = await stopPermanentPayment(expenseId);
            
            if (result.success) {
                setMessage({ 
                    type: 'success', 
                    text: `התשלום הקבוע ל${supplierName} הופסק בהצלחה` 
                });

                // Remove expense from displayed list
                setDisplayedExpenses(prev => 
                    prev.filter(expense => 
                        (expense.expenseID || expense.ExpenseID) !== expenseId
                    )
                );
                
                // Remove from selected expenses if it was selected
                setSelectedExpenses(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(expenseId);
                    return newSet;
                });
                
                // Remove from invoice numbers
                setInvoiceNumbers(prev => {
                    const newNumbers = { ...prev };
                    delete newNumbers[expenseId];
                    return newNumbers;
                });

                // Notify parent component
                if (onExpensesCreated) {
                    onExpensesCreated([{ ExpenseID: expenseId, stopped: true }]);
                }
            } else {
                setMessage({ type: 'error', text: 'שגיאה בהפסקת התשלום הקבוע' });
            }
        } catch (error) {
            console.error('Error stopping payment:', error);
            setMessage({ type: 'error', text: 'שגיאה בהפסקת התשלום הקבוע' });
        } finally {
            setStoppingPayments(prev => {
                const newSet = new Set(prev);
                newSet.delete(expenseId);
                return newSet;
            });
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'לא זמין';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'לא זמין';
        return date.toLocaleDateString('he-IL', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const currentMonth = new Date().toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long'
    });

    return (
        <div className={styles.overlay}>
            <div className={styles.popup}>
                <div className={styles.header}>
                    <div className={styles.titleSection}>
                        <AlertCircle className={styles.titleIcon} />
                        <div>
                            <h2 className={styles.title}>הוצאות קבועות לחודש {currentMonth}</h2>
                            <p className={styles.subtitle}>
                                נמצאו {displayedExpenses.length} הוצאות קבועות שטרם שולמו החודש
                            </p>
                            <p className={`${styles.subtitle} ${styles.requiredMessage}`} style={{ fontSize: '0.875rem', marginTop: '0.25rem', color: '#ef4444' }}>
                                * חובה להזין מספר חשבונית לכל הוצאה שנבחרה
                            </p>
                        </div>
                    </div>
                    <button className={styles.closeButton} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {message.text && (
                    <div className={`${styles.message} ${styles[message.type]}`}>
                        {message.text}
                    </div>
                )}

                <div className={styles.content}>
                    <div className={styles.expensesList}>
                        {displayedExpenses.map((expense) => (
                            <div key={expense.expenseID || expense.ExpenseID} className={styles.expenseItem}>
                                <div className={styles.expenseContent}>
                                    <div className={styles.checkboxSection}>
                                        <input
                                            type="checkbox"
                                            id={`expense-${expense.expenseID || expense.ExpenseID}`}
                                            className={styles.checkbox}
                                            checked={selectedExpenses.has(expense.expenseID || expense.ExpenseID)}
                                            onChange={() => handleExpenseToggle(expense.expenseID || expense.ExpenseID)}
                                        />
                                        <label 
                                            htmlFor={`expense-${expense.expenseID || expense.ExpenseID}`}
                                            className={styles.checkboxLabel}
                                        >
                                            <CheckSquare size={20} />
                                        </label>
                                    </div>

                                    <div className={styles.expenseDetails}>
                                        <div className={styles.expenseHeader}>
                                            <h3 className={styles.expenseTitle}>{expense.description || expense.Description}</h3>
                                            <span className={styles.expenseAmount}>
                                                {formatCurrency(expense.amount || expense.Amount)}
                                            </span>
                                        </div>

                                        <div className={styles.expenseInfo}>
                                            <div className={styles.infoItem}>
                                                <User size={16} />
                                                <span>לתשלום עבור: {expense.paidTo || expense.vendor || expense.PaidTo || expense.Vendor || 'לא צוין'}</span>
                                            </div>
                                            
                                            <div className={styles.infoItem}>
                                                <Calendar size={16} />
                                                <span>תשלום אחרון: {formatDate(expense.date || expense.ExpenseDate || expense.Date)}</span>
                                            </div>

                                            <div className={styles.infoItem}>
                                                <DollarSign size={16} />
                                                <span>קטגוריה: {expense.category || expense.expenseType || expense.Category || 'כללי'}</span>
                                            </div>
                                            
                                            <div className={styles.formRow}>
                                                <div className={styles.formField}>
                                                    <label htmlFor={`invoice-${expense.expenseID || expense.ExpenseID}`}>
                                                        מספר חשבונית: <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id={`invoice-${expense.expenseID || expense.ExpenseID}`}
                                                        className={styles.invoiceInput}
                                                        placeholder="חובה להכניס מספר חשבונית"
                                                        value={invoiceNumbers[expense.expenseID || expense.ExpenseID] || '0'}
                                                        onChange={(e) => handleInvoiceNumberChange(expense.expenseID || expense.ExpenseID, e.target.value)}
                                                        disabled={!selectedExpenses.has(expense.expenseID || expense.ExpenseID)}
                                                        required
                                                        style={{
                                                            borderColor: selectedExpenses.has(expense.expenseID || expense.ExpenseID) && 
                                                                       (!invoiceNumbers[expense.expenseID || expense.ExpenseID] || 
                                                                        invoiceNumbers[expense.expenseID || expense.ExpenseID].trim() === '') 
                                                                       ? '#ef4444' : undefined
                                                        }}
                                                    />
                                                </div>
                                                
                                                <div className={styles.formField}>
                                                    <label htmlFor={`payment-${expense.expenseID || expense.ExpenseID}`}>
                                                        אמצעי תשלום:
                                                    </label>
                                                    <select
                                                        id={`payment-${expense.expenseID || expense.ExpenseID}`}
                                                        className={styles.paymentSelect}
                                                        value={paymentMethods[expense.expenseID || expense.ExpenseID] || 'מזומן'}
                                                        onChange={(e) => handlePaymentMethodChange(expense.expenseID || expense.ExpenseID, e.target.value)}
                                                        disabled={!selectedExpenses.has(expense.expenseID || expense.ExpenseID)}
                                                    >
                                                        {paymentMethodOptions.map(method => (
                                                            <option key={method} value={method}>{method}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.actions}>
                                        <button
                                            className={styles.stopButton}
                                            onClick={() => handleStopPayment(
                                                expense.expenseID || expense.ExpenseID, 
                                                expense.paidTo || expense.vendor || expense.PaidTo || expense.Vendor || 'ספק'
                                            )}
                                            disabled={stoppingPayments.has(expense.expenseID || expense.ExpenseID)}
                                            title="הפסק תשלום קבוע"
                                        >
                                            {stoppingPayments.has(expense.expenseID || expense.ExpenseID) ? (
                                                <Loader2 size={16} className={styles.spinning} />
                                            ) : (
                                                <X size={16} />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.summary}>
                        <div className={styles.summaryStats}>
                            <div className={styles.stat}>
                                <span className={styles.statLabel}>סך הכל הוצאות:</span>
                                <span className={styles.statValue}>{displayedExpenses.length}</span>
                            </div>
                            <div className={styles.stat}>
                                <span className={styles.statLabel}>נבחרו:</span>
                                <span className={styles.statValue}>{selectedExpenses.size}</span>
                            </div>
                            <div className={styles.stat}>
                                <span className={styles.statLabel}>סכום כולל:</span>
                                <span className={styles.statValue}>
                                    {formatCurrency(
                                        displayedExpenses
                                            .filter(expense => selectedExpenses.has(expense.expenseID || expense.ExpenseID))
                                            .reduce((sum, expense) => sum + (expense.amount || expense.Amount || 0), 0)
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.footer}>
                    <div className={styles.footerActions}>
                        <button
                            className={styles.selectAllButton}
                            onClick={handleSelectAll}
                            disabled={isCreating}
                        >
                            {selectedExpenses.size === displayedExpenses.length ? 'בטל בחירת הכל' : 'בחר הכל'}
                        </button>

                        <button
                            className={styles.cancelButton}
                            onClick={onClose}
                            disabled={isCreating}
                        >
                            ביטול
                        </button>

                        <button
                            className={styles.createButton}
                            onClick={handleCreateExpenses}
                            disabled={selectedExpenses.size === 0 || isCreating}
                        >
                            {isCreating ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Loader2 size={16} className={styles.spinning} />
                                    יוצר הוצאות...
                                </span>
                            ) : (
                                `צור ${selectedExpenses.size} הוצאות לחודש הנוכחי`
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecurringExpensesPopup;